import React, { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { entityKycStatusAction } from "../../actions/entityKycStatusAction";
import {
  useUploadDetails,
  ENTITY_INFO_FIELDS,
  GST_FIELDS,
  MSME_FIELDS,
  CONTACT_FIELDS,
  BANK_FIELDS,
  DOCS_BANK,
  renderFields,
  EntityDocumentsSection,
} from "./UploadDetailsData";
import GSTEntries from "./Components/GSTEntries";
import UtilityForm from "./Components/UtilityForm";
import MSMESection from "./Components/MSMESection";
import ThirdPartySection from "./Components/ThirdPartySection";
import DocumentUpload from "../../components/DocumentUpload";
import { canUploadDoc } from "../../utils/deficiencyUtils";
import companyLogo from "../../assets/images/AcrossLogo.png";
import "./index.scss";

const UploadDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Check if KYC already submitted — redirect to thank-you if so
  useEffect(() => {
    const id =
      searchParams.get("onboardingId") || searchParams.get("entityPrimaryId");
    if (!id) return;
    entityKycStatusAction(parseInt(id, 10)).then(({ isDocumentUploaded }) => {
      if (isDocumentUploaded) {
        const entityName = searchParams.get("EntityName") || "";
        const entityId =
          searchParams.get("entityId") || searchParams.get("EntityId") || "";
        navigate(
          `/thank-you?entityName=${encodeURIComponent(entityName)}&entityId=${encodeURIComponent(entityId)}&alreadySubmitted=true`,
          { replace: true },
        );
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    formData,
    errors,
    setErrors,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    handleSaveDraft,
    handleDocumentUpload,
    gstEntries,
    addGstEntry,
    removeGstEntry,
    handleGstChange,
    handleGstBlur,
    gstFiles,
    handleGstFileChange,
    gstErrors,
    utilityFile,
    setUtilityFile,
    msmeFile,
    setMsmeFile,
    thirdPartyFile,
    setThirdPartyFile,
    entityFiles,
    handleEntityFileChange,
    entityDocInputs,
    handleEntityDocInputChange,
    handleEntityDocInputBlur,
    bankFiles,
    handleBankFileChange,
    entityId,
    showThirdParty,
    // beneficiaryMatchStatus, entityName — not used in JSX currently
    existingDocuments,
    deficiencyMap,
    isDeficiencyMode,
    // entityCategory, entityLOBType, contactPersonEmail, spocEmailAddress, // TODO: show in header
  } = useUploadDetails();

  // In deficiency mode: all fields readonly except deficient ones
  const formReadOnly = isDeficiencyMode;

  // Helper: get existing uploaded file for a doc key
  const getExistingFile = (docKey) => {
    const doc = existingDocuments.find((d) => d.key === docKey);
    return doc?.files?.find((f) => f.isUploaded) || null;
  };

  // Can a doc be re-uploaded?
  const docUploadable = (docKey) =>
    canUploadDoc(docKey, deficiencyMap, isDeficiencyMode);

  return (
    <div className="upload-details-page">
      {/* ── Header ── */}
      <div className="page-header">
        <div className="page-header__brand">
          <img
            src={companyLogo}
            alt="Company logo"
            className="page-header__logo"
          />
        </div>
        <div className="page-header__text">
          <h1 className="page-title">Complete KYC OnBoarding Details</h1>
          <p className="page-subtitle">
            Fill in the remaining details to complete onboarding registration
          </p>
        </div>
      </div>

      {/* ── Single form card ── */}
      <form onSubmit={handleSubmit} className="details-form">
        <div className="form-card">
          {/* Deficiency mode banner */}
          {isDeficiencyMode && (
            <div className="deficiency-banner">
              <span className="deficiency-banner__icon">⚠️</span>
              <div>
                <strong>Deficiency Notice</strong>
                <p>
                  Some documents/fields have been marked as deficient. Only the
                  highlighted fields (in red) can be updated. All other fields
                  are read-only.
                </p>
              </div>
            </div>
          )}

          {/* Entity Information */}
          <section className="form-section">
            <h2 className="section-title">KYC Information - {entityId}</h2>
            <div className="form-grid">
              {renderFields(
                ENTITY_INFO_FIELDS,
                formData,
                errors,
                formReadOnly ? undefined : handleChange,
                formReadOnly ? undefined : handleBlur,
                formReadOnly,
              )}
            </div>
            <EntityDocumentsSection
              entityType={formData.entityType}
              entityFiles={entityFiles}
              onEntityFileChange={(key, file) => {
                if (!docUploadable(key)) return;
                handleEntityFileChange(key, file);
                handleDocumentUpload(key, file);
              }}
              entityDocInputs={entityDocInputs}
              onEntityDocInputChange={
                formReadOnly ? undefined : handleEntityDocInputChange
              }
              onEntityDocInputBlur={
                formReadOnly ? undefined : handleEntityDocInputBlur
              }
              errors={errors}
              existingDocuments={existingDocuments}
              deficiencyMap={deficiencyMap}
              isDeficiencyMode={isDeficiencyMode}
            />
          </section>

          <div className="section-divider" />

          {/* Contact Person */}
          <section className="form-section">
            <h2 className="section-title">Contact Person Details</h2>
            <div className="form-grid">
              {renderFields(
                CONTACT_FIELDS,
                formData,
                errors,
                formReadOnly ? undefined : handleChange,
                formReadOnly ? undefined : handleBlur,
                formReadOnly,
              )}
            </div>
          </section>

          <div className="section-divider" />

          {/* GST Information */}
          <section className="form-section">
            <h2 className="section-title">GST Information</h2>
            <div className="form-grid">
              {renderFields(
                GST_FIELDS,
                formData,
                errors,
                formReadOnly ? undefined : handleChange,
                formReadOnly ? undefined : handleBlur,
                formReadOnly,
              )}
            </div>
            <GSTEntries
              isGSTRegistered={formData.isGSTRegistered}
              gstEntries={gstEntries}
              addGstEntry={formReadOnly ? undefined : addGstEntry}
              removeGstEntry={formReadOnly ? undefined : removeGstEntry}
              handleGstChange={formReadOnly ? undefined : handleGstChange}
              handleGstBlur={formReadOnly ? undefined : handleGstBlur}
              gstFiles={gstFiles}
              handleGstFileChange={(index, file) => {
                const key = `gst_file_${index}`;
                if (!docUploadable(key)) return;
                handleGstFileChange(index, file);
                handleDocumentUpload(key, file);
                if (file)
                  setErrors((prev) => {
                    const e = { ...prev };
                    delete e[`gst_certificate_${index}`];
                    return e;
                  });
              }}
              gstCertErrors={errors}
              gstErrors={gstErrors}
              readOnly={formReadOnly}
              existingGstFiles={existingDocuments
                .filter((d) => d.key.startsWith("gst_file_"))
                .sort((a, b) => {
                  const ai = parseInt(a.key.replace("gst_file_", ""), 10);
                  const bi = parseInt(b.key.replace("gst_file_", ""), 10);
                  return ai - bi;
                })
                .map((d) => d.files?.find((f) => f.isUploaded) || null)}
            />
            <UtilityForm
              isGSTRegistered={formData.isGSTRegistered}
              formData={formData}
              errors={errors}
              handleChange={formReadOnly ? undefined : handleChange}
              handleBlur={formReadOnly ? undefined : handleBlur}
              utilityFile={utilityFile}
              handleUtilityFileChange={(file) => {
                if (!docUploadable("doc_utility_bill")) return;
                setUtilityFile(file);
                handleDocumentUpload("utility_file", file);
              }}
              existingUtilityFile={getExistingFile("doc_utility_bill")}
              readOnly={formReadOnly}
              isDeficient={!!deficiencyMap["doc_utility_bill"]}
            />
          </section>

          <div className="section-divider" />

          {/* MSME Information */}
          <section className="form-section">
            <h2 className="section-title">MSME Information</h2>
            <div className="form-grid">
              {renderFields(
                MSME_FIELDS,
                formData,
                errors,
                formReadOnly ? undefined : handleChange,
                formReadOnly ? undefined : handleBlur,
                formReadOnly,
              )}
            </div>
            <MSMESection
              isMSMERegistered={formData.isMSMERegistered}
              formData={formData}
              errors={errors}
              handleChange={formReadOnly ? undefined : handleChange}
              handleBlur={formReadOnly ? undefined : handleBlur}
              msmeFile={msmeFile}
              handleMsmeFileChange={(file) => {
                if (!docUploadable("doc_msme_certificate")) return;
                setMsmeFile(file);
                handleDocumentUpload("msme_file", file);
              }}
              existingMsmeFile={getExistingFile("doc_msme_certificate")}
              readOnly={formReadOnly}
              isDeficient={!!deficiencyMap["doc_msme_certificate"]}
            />
          </section>

          <div className="section-divider" />

          {/* Bank Details */}
          <section className="form-section">
            <h2 className="section-title">Bank Details</h2>
            <div className="form-grid-radio">
              <label className="radio-label">
                Provide Bank Details?<span className="required-mark"> *</span>
              </label>
              <div className="radio-group">
                {["yes", "no"].map((v) => (
                  <label key={v} className="radio-option">
                    <input
                      type="radio"
                      name="hasBankDetails"
                      value={v}
                      checked={formData.hasBankDetails === v}
                      onChange={formReadOnly ? undefined : handleChange}
                      disabled={formReadOnly}
                    />
                    {v === "yes" ? "Yes" : "No"}
                  </label>
                ))}
              </div>
              {errors.hasBankDetails && (
                <span className="input-error-msg">{errors.hasBankDetails}</span>
              )}
            </div>
            {formData.hasBankDetails === "yes" && (
              <>
                <div className="form-grid">
                  {renderFields(
                    BANK_FIELDS,
                    formData,
                    errors,
                    formReadOnly ? undefined : handleChange,
                    formReadOnly ? undefined : handleBlur,
                    formReadOnly,
                  )}
                </div>
                {/* Bank Documents */}
                <div className="entity-docs-section">
                  <p className="entity-docs-note">Required Bank Documents</p>
                  <div className="entity-docs-grid">
                    {DOCS_BANK.map((doc) => {
                      const uploadable = docUploadable(doc.key);
                      const deficient = !!deficiencyMap[doc.key];
                      return (
                        <div
                          key={doc.key}
                          className={deficient ? "doc-deficient-wrap" : ""}
                        >
                          <DocumentUpload
                            label={doc.label}
                            name={doc.key}
                            file={bankFiles[doc.key] || null}
                            existingFile={getExistingFile(doc.key)}
                            onChange={(e) => {
                              if (!uploadable) return;
                              const file = e.target.files[0];
                              handleBankFileChange(doc.key, file);
                              handleDocumentUpload(doc.key, file);
                              if (file)
                                setErrors((prev) => {
                                  const err = { ...prev };
                                  delete err[doc.key];
                                  return err;
                                });
                            }}
                            required={doc.required}
                            error={errors[doc.key] || ""}
                            disabled={!uploadable}
                          />
                          {deficient && (
                            <span className="doc-deficient-label">
                              ⚠ Deficiency — please re-upload
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <ThirdPartySection
                  show={showThirdParty}
                  formData={formData}
                  errors={errors}
                  handleChange={formReadOnly ? undefined : handleChange}
                  handleBlur={formReadOnly ? undefined : handleBlur}
                  thirdPartyFile={thirdPartyFile}
                  handleThirdPartyFileChange={(file) => {
                    if (!docUploadable("third_party_file")) return;
                    setThirdPartyFile(file);
                    handleDocumentUpload("third_party_file", file);
                  }}
                />
              </>
            )}
          </section>

          {/* ── Submit footer inside the card ── */}
          <div className="form-footer">
            <button
              type="button"
              className="btn-save-draft"
              disabled={isSubmitting}
              onClick={handleSaveDraft}
            >
              {isSubmitting ? "Saving…" : "Save Draft"}
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UploadDetails;
